import { useCallback, useMemo, useState } from "react"
import { GalleryImage } from "@/features/gallery/hooks/useGalleryImages"

const makeKey = (img: GalleryImage) => img.assetId

const useGallerySelection = (allImages: GalleryImage[]) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())

  const isSelected = useCallback((img: GalleryImage) => selectedKeys.has(makeKey(img)), [selectedKeys])

  const toggle = useCallback((img: GalleryImage) => {
    setSelectedKeys(prev => {
      const next = new Set(prev)
      const key = makeKey(img)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const toggleDate = useCallback((images: GalleryImage[]) => {
    setSelectedKeys(prev => {
      const allSelected = images.every(img => prev.has(makeKey(img)))
      const next = new Set(prev)
      if (allSelected) {
        images.forEach(img => next.delete(makeKey(img)))
      } else {
        images.forEach(img => next.add(makeKey(img)))
      }
      return next
    })
  }, [])

  const isDateAllSelected = useCallback(
    (images: GalleryImage[]) => images.length > 0 && images.every(img => selectedKeys.has(makeKey(img))),
    [selectedKeys]
  )

  const isDateSomeSelected = useCallback(
    (images: GalleryImage[]) =>
      images.some(img => selectedKeys.has(makeKey(img))) && !images.every(img => selectedKeys.has(makeKey(img))),
    [selectedKeys]
  )

  const selectAll = useCallback(() => {
    setSelectedKeys(new Set(allImages.map(makeKey)))
  }, [allImages])

  const clearAll = useCallback(() => {
    setSelectedKeys(new Set())
  }, [])

  const selectedImages = useMemo(
    () => allImages.filter(img => selectedKeys.has(makeKey(img))),
    [allImages, selectedKeys]
  )

  return {
    isSelected,
    toggle,
    toggleDate,
    isDateAllSelected,
    isDateSomeSelected,
    selectAll,
    clearAll,
    selectedCount: selectedKeys.size,
    selectedImages
  }
}

export { useGallerySelection }
